import { modelState, ModelStateList } from "@/utils/modelState";
import { dateEqualityComparer } from "@/utils/utils";
import { add, addDays, startOfDay } from 'date-fns';
import { buildCompanyCacheFromPreloadedContacts } from "./CachedCompanyProfileState";
import { initializeProfileCache } from "./CachedPersonProfileState";
import { createInquirySourceState } from './InquirySources/inquirySourceState';
import { PaymentTypes } from "./PaymentTypes/PaymentType";
import { personProfileLoader } from "./PersonProfileState";
import { RefundTypes } from "./RefundTypes/RefundType";
import { deserializeReservationArrivalTransferMode } from "./ReservationArrivalTransferMode";
import { deserializeReservationDepartureTransferMode } from "./ReservationDepartureTransferMode";
import { deserializeReservationLogEntry } from "./ReservationLogEntry";
import { ReservationQuoteStatuses } from "./ReservationQuoteStatuses/ReservationQuoteStatus";
import { TaxDocumentTypes } from "./TaxDocumentTypes/TaxDocumentType";
import { ReservationDiffToReservationDiff } from "./model.Diffs";
import { addProgramBooking, ProgramBookingState, ProgramBookingToProgramBookingState } from "./model.Programs";
import { ReservationExistingPerson, ReservationPersonCache } from "./model.ReservationPerson";
import { RoomBookingToRoomBookingState } from "./model.Rooms";
import type { ModelContext } from "./model.context";
import { addRoomBooking, calculateDates } from './model.roomsAndTotals';
import { PersonDto, ReservationProfileCacheDto, RoomBlockDate, RoomBlockDateRoom, saveReservation, ServerPersonCreditDto } from './model.saveAndHistory';
import { hydrateRevenueItem } from "./revenueHydration";
import { SaveProgressInfo } from "./saveProgress";
import type { NewCompanyDto, RoomBookingState } from "./types";
import { ReservationCompany, ReservationExistingCompany, ReservationPerson } from "./types";
import { isBeforeBangkokToday, PosItemToPosItemState } from "./utils";
export function initializeModel0(context: ModelContext) {
    context.roomBookingStates = context.roomBookings.map<RoomBookingState>(rb => RoomBookingToRoomBookingState(rb));
    context.programBookingStates = context.programBookings.map<ProgramBookingState>(rb => ProgramBookingToProgramBookingState(rb));
    // Sort rooms and programs by date to maintain chronological chain order
    context.roomBookingStates.sort((a, b) => a.CheckIn.value.getTime() - b.CheckIn.value.getTime());
    context.programBookingStates.sort((a, b) => a.Start.value.getTime() - b.Start.value.getTime());
    context.dates = calculateDates(context.roomBookingStates, context.programBookingStates, startOfDay(new Date()), add(startOfDay(new Date()), { days: 1 }));


    if (context.personId) {
        const existingPerson: ReservationExistingPerson = {
            [ReservationPerson]: true,
            [ReservationExistingPerson]: true,
            PersonId: context.personId,
            ContactId: null
        };
        context.person = modelState(existingPerson);
    }
    else {
        context.person = modelState(null);
    }
    // Prefer preloadedContacts for type-aware construction (Person or Company)
    context.billingContact = modelState(null);
    if (context.billingContactId) {
        let c: any = null;
        if (context.preloadedContacts && context.preloadedContacts[context.billingContactId]) {
            c = context.preloadedContacts[context.billingContactId];
        }
        else if (context.preloadedContacts) {
            // Fallback: search by ContactId field within values
            const values = Object.values(context.preloadedContacts) as any[];
            c = values.find(v => v && v.ContactId === context.billingContactId);
        }
        if (c && c.$type === "Person" && c.PersonId) {
            const existingBillingPerson: ReservationExistingPerson = {
                [ReservationPerson]: true,
                [ReservationExistingPerson]: true,
                PersonId: c.PersonId,
                ContactId: c.ContactId ?? null
            };
            context.billingContact = modelState(existingBillingPerson as any);
        }
        else if (c && c.$type === "Company" && c.CompanyId) {
            const existingBillingCompany = {
                [ReservationCompany]: true,
                [ReservationExistingCompany]: true,
                CompanyId: c.CompanyId,
                ContactId: (c.Company?.ContactId ?? c.ContactId) ?? null
            };
            context.billingContact = modelState(existingBillingCompany as any);
        }
        else {
            // If we can't determine the type from preloaded contacts, leave null
            context.billingContact = modelState(null);
        }
    }
    else {
        context.billingContact = modelState(null);
    }

    if (context.reservationId) {
        context.arrivalDate = modelState(context.dates.arrivalDate, dateEqualityComparer);
    }
    else {
        context.arrivalDate = modelState(null, dateEqualityComparer);
        context.arrivalDate.setValue(context.dates.arrivalDate);
    }
    context.personCache = new ReservationPersonCache();
    context.reservationRef = { current: null };
    context.companyCache = buildCompanyCacheFromPreloadedContacts(context.preloadedContacts || {}, (error: string) => {
        if (context.reservationRef.current) {
            context.reservationRef.current.LoadingError.setValue(error);
            context.reservationRef.current.HasCriticalError.setValue(true);
        }
    });
    context.newCompanies = new ModelStateList<NewCompanyDto>([]);
    // Initialize the global profile cache with preloaded profiles
    if (context.preloadedProfiles) {
        // Convert from full PersonWithCredits format to the expected ReservationProfileCacheDto format
        const convertedProfiles: {
            [personId: string]: ReservationProfileCacheDto;
        } = {};
        Object.entries(context.preloadedProfiles).forEach(([personId, profile]) => {
            convertedProfiles[personId] = {
                PersonId: profile.PersonId,
                Credits: profile.Credits.map((c: ServerPersonCreditDto) => ({
                    CreditId: c.CreditId,
                    Amount: c.Amount,
                    ExpiresOn: c.ExpiresOn,
                    SequenceNumber: c.SequenceNumber
                }))
            };
        });
        initializeProfileCache(convertedProfiles);
        // Preload person details into the reservation-specific cache
        // Convert from full PersonWithCredits format to PersonDto format
        const personDetails: {
            [personId: string]: PersonDto;
        } = {};
        Object.entries(context.preloadedProfiles).forEach(([personId, profile]) => {
            personDetails[personId] = {
                PersonId: profile.PersonId,
                ContactId: profile.ContactId,
                FirstName: profile.FirstName,
                LastName: profile.LastName,
                PhoneNumber: profile.PhoneNumber,
                Email: profile.Email,
                Credits: profile.Credits.map((c: ServerPersonCreditDto) => ({
                    CreditId: c.CreditId,
                    Amount: c.Amount,
                    ExpiresOn: new Date(c.ExpiresOn),
                    SequenceNumber: c.SequenceNumber
                }))
            };
        });
        // Preload into the reservation person cache
        context.personCache.preloadPersonDetails(personDetails);
    }
    context.profileLoader = personProfileLoader(context.person);
    context.loadedHasPastDate = context.roomBookingStates.some(rb => rb.CheckIn.value && isBeforeBangkokToday(rb.CheckIn.value)) ||
        context.programBookingStates.some(pb => pb.Start.value && isBeforeBangkokToday(pb.Start.value));
    context.reservation = {
        ReservationId: context.reservationId,
        InquirySource: createInquirySourceState(context.inquirySourceData),
        ReservationNumber: context.reservationNumber,
        Person: context.person,
        BillingContact: context.billingContact,
        Profile: context.profileLoader.result,
        ArrivalDate: context.arrivalDate,
        DepartureDate: modelState(context.dates.departureDate, dateEqualityComparer),
        ArrivalDateCalculated: modelState(context.dates.arrivalDate, dateEqualityComparer),
        DepartureDateCalculated: modelState(context.dates.departureDate, dateEqualityComparer),
        DatesEnabled: modelState(context.dates.datesEnabled),
        AllowPastDates: modelState(context.loadedHasPastDate),
        RoomBookings: new ModelStateList(context.roomBookingStates),
        SomeRoomBookingsDirty: modelState(false),
        SomeRoomBookingsHaveMissingMandatoryFields: modelState(false),
        addRoomBooking: () => addRoomBooking(context.reservation),
        IsDirty: modelState(!context.reservationId),
        ProgramBookings: new ModelStateList(context.programBookingStates),
        SomeProgramBookingsDirty: modelState(false),
        addProgramBooking: () => addProgramBooking(context.reservation),
        Vouchers: context.vouchers,
        AppliedVouchers: modelState(context.appliedVouchers),
        Payments: new ModelStateList(context.payments.map(p => ({
            CreatedDate: new Date(p.CreatedDate),
            PaymentId: p.PaymentId,
            PaymentType: PaymentTypes.deserializePaymentType(p.PaymentType),
            Amount: p.Amount,
            Transactions: p.Transactions.map(t => ({
                CreatedDate: new Date(t.CreatedDate),
                EffectiveDate: t.EffectiveDate,
                Amount: t.Amount,
                Note: t.Note,
                TaxDocument: TaxDocumentTypes.deserializeTaxDocumentType(t.TaxDocument),
                IsVoid: t.IsVoid === true,
                JournalDocumentNumber: t.JournalDocumentNumber ?? null,
                ExternalPostingStatus: t.ExternalPostingStatus ?? null,
            }))
        }))),
        Refunds: new ModelStateList(context.refunds.map(p => ({
            CreatedDate: new Date(p.CreatedDate),
            RefundId: p.RefundId,
            RefundType: RefundTypes.deserializeRefundType(p.RefundType),
            Amount: p.Amount,
            Transactions: p.Transactions.map(t => ({
                CreatedDate: new Date(t.CreatedDate),
                EffectiveDate: t.EffectiveDate,
                Amount: t.Amount,
                Note: t.Note,
                TaxDocument: TaxDocumentTypes.deserializeTaxDocumentType(t.TaxDocument),
                IsVoid: t.IsVoid === true,
                JournalDocumentNumber: t.JournalDocumentNumber ?? null,
                ExternalPostingStatus: t.ExternalPostingStatus ?? null,
            }))
        }))),
        Revenue: context.revenue.map(hydrateRevenueItem),
        PosItems: modelState(context.posItems.map(pi => PosItemToPosItemState(pi))),
        AmountDueTotal: modelState(0),
        AmountPayableTotal: modelState(0),
        ReservationStatus: modelState(null),
        ReservationStatusIsDirty: modelState(false),
        ReservationStatusVersion: modelState(0),
        Quote: modelState(context.quoteDto ?
            {
                ExpiresAt: modelState(new Date(context.quoteDto.expiresAt)),
                Status: modelState(ReservationQuoteStatuses.deserializeReservationQuoteStatus(context.quoteDto.status)),
                StatusIsDirty: modelState(false),
                StatusVersion: modelState(0),
                TempReservationEnabled: modelState(context.quoteDto.tempReservationEnabled ?? false),
                TempReservationReleaseAfter: modelState(context.quoteDto.tempReservationReleaseAfter
                    ? new Date(context.quoteDto.tempReservationReleaseAfter)
                    : null),
            }
            : {
                ExpiresAt: modelState(addDays(new Date(), 3)),
                Status: modelState(ReservationQuoteStatuses.createReservationQuoteInitialEnquiryStatus()),
                StatusIsDirty: modelState(false),
                StatusVersion: modelState(0),
                TempReservationEnabled: modelState(false),
                TempReservationReleaseAfter: modelState<Date | null>(null)
            }),
        QuoteIsDirty: modelState(false),
        QuoteVersion: modelState(0),
        Notes: new ModelStateList(context.notes),
        LogEntries: new ModelStateList(context.logEntries.map(l => ({
            LogEntryId: l.LogEntryId,
            LogText: l.LogText,
            LogData: deserializeReservationLogEntry(l.LogData),
            CreatedBy: l.CreatedBy,
            CreatedAt: l.CreatedAt
        }))),
        ArrivalTransferDetails: {
            Mode: context.arrivalTransferDetails ? modelState(deserializeReservationArrivalTransferMode(context.arrivalTransferDetails.mode)) : modelState(null),
        },
        ArrivalTransferDetailsIsDirty: modelState(false),
        ArrivalTransferDetailsVersion: modelState(0),
        DepartureTransferDetails: {
            Mode: context.departureTransferDetails ? modelState(deserializeReservationDepartureTransferMode(context.departureTransferDetails.mode)) : modelState(null),
        },
        DepartureTransferDetailsIsDirty: modelState(false),
        DepartureTransferDetailsVersion: modelState(0),
        NewPeople: new ModelStateList([]),
        PersonCache: context.personCache,
        NewCompanies: context.newCompanies,
        CompanyCache: context.companyCache,
        LoadingError: modelState<string | null>(null),
        HasCriticalError: modelState(false),
        RoomTypes: context.roomTypes,
        ProgramTypes: {},
        OccupantTypes: {
            'Adult': {
                Key: 'Adult',
                Name: 'Adult',
            },
            'Child': {
                Key: 'Child',
                Name: 'Child',
            },
            'Infant': {
                Key: 'Infant',
                Name: 'Infant',
            }
        },
        RoomBlocks: {
            Dates: new ModelStateList<RoomBlockDate>(context.roomBlockDates.map(rbd => ({
                Date: startOfDay(new Date(rbd.Date)),
                Rooms: new ModelStateList<RoomBlockDateRoom>(rbd.Rooms.map(r => ({
                    LocationId: r.LocationId,
                    IsAllDay: r.IsAllDay,
                    Start: new Date(r.Start),
                    End: new Date(r.End),
                    ReservationRoomId: r.ReservationRoomId
                }))),
                Version: modelState(0)
            }))),
            Version: modelState(0)
        },
        RevenueLockDate: context.revenueLockDate ? new Date(context.revenueLockDate) : null,
        CreatedUtcOverride: modelState<Date | null>(null),
        Rates: context.rates,
        SelectedRatePlan: modelState({
            RatePlanId: context.selectedRatePlanId,
            RatePlanVersion: context.selectedRatePlanVersion
        }),
        RatePlanFormResponseJson: context.ratePlanFormResponseJson,
        // check room booking states either empty or all are available:
        AreAllRoomsAvailable: modelState(context.roomBookingStates.length === 0 || context.roomBookingStates.every(rb => rb.AvailabilityCheckResult.value.IsAvailable === true || rb.NoShow.value === true)),
        // check program booking states either empty or all are available (or overbooking allowed):
        AreAllProgramsAvailable: modelState(context.programBookingStates.length === 0 || context.programBookingStates.every(pb => pb.AvailabilityCheckResult.value.IsAvailable === true || pb.NoShow.value === true || pb.AllowOverbooking.value === true)),
        OrderDiffs: modelState(context.orderDiffs.map(d => ReservationDiffToReservationDiff(d))),
        IsPriceLoading: modelState(false),
        save: async (newStatus) => await saveReservation(context.reservation, newStatus ?? null),
        IsLoading: modelState(false),
        SaveProgress: modelState<SaveProgressInfo | null>(null),
        closed: function () {
            // Clear the person cache when reservation is closed
            context.personCache.clear();
            context.companyCache.clear();
            if ((window as any).salesSchedule) {
                (window as any).salesSchedule.closed();
            }
        },
        checkPrices: context.checkPrices,
        checkProgramsWarning: context.checkProgramsWarning,
        checkRoomsWarning: context.checkRoomsWarning,
        RoomsWarningText: modelState(null),
        ProgramsWarningText: modelState(null),
        CanRelease: modelState(true),
        MacroConfigurations: [],
        unsubscribeDates: () => { },
        MergeableRoomClusters: context.mergeableRoomClusters ?? []
    };
    // Bind reservation for company cache error reporting
    context.reservationRef.current = context.reservation;
}
